import React, { useEffect, useState } from 'react';
import { federation } from '../../utils/api';
import { track, FEDERATION_EXPORT } from '../../utils/analytics';
import FederationGate from './FederationGate';
import {
  EXPORT_TYPES,
  ErrorBanner,
  LoadingBlock,
  PageHeader,
  btnPrimary,
  btnSecondary,
  inputClass,
  safeText,
} from './federationShared';

function ReportSection({ title, children }) {
  if (!children) return null;
  return (
    <section className="report-section border border-navy-200 rounded-xl bg-white p-5 mb-4 break-inside-avoid">
      <h2 className="text-base font-semibold text-navy-900 mb-3">{title}</h2>
      {children}
    </section>
  );
}

function ListOrEmpty({ items, renderItem, empty }) {
  if (!Array.isArray(items) || items.length === 0) {
    return <p className="text-sm text-gray-500">{empty}</p>;
  }
  return <ul className="space-y-2">{items.map(renderItem)}</ul>;
}

function renderValueRows(obj) {
  if (!obj || typeof obj !== 'object') return null;
  const entries = Object.entries(obj).filter(([, v]) => v == null || typeof v !== 'object');
  if (entries.length === 0) return null;
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
      {entries.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-3 border-b border-navy-50 py-1">
          <dt className="text-gray-500">{k}</dt>
          <dd className="text-navy-900 font-medium text-right">{safeText(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

function ReportsInner() {
  const [programs, setPrograms] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [type, setType] = useState('program_summary');
  const [programId, setProgramId] = useState('');
  const [periodId, setPeriodId] = useState('');
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);

  useEffect(() => {
    setLoadingMeta(true);
    Promise.all([
      federation.getPrograms().catch(() => ({ programs: [] })),
      federation.getPeriods().catch(() => ({ periods: [] })),
    ])
      .then(([p, per]) => {
        setPrograms(p.programs || []);
        setPeriods(per.periods || []);
      })
      .finally(() => setLoadingMeta(false));
  }, []);

  const generate = async () => {
    setLoading(true);
    setError('');
    setReport(null);
    try {
      const params = { type };
      if (programId) params.programId = programId;
      if (periodId) params.periodId = periodId;
      const res = await federation.getExport(params);
      setReport(res.report || res || null);
      track(FEDERATION_EXPORT, { type });
    } catch (e) {
      setError(e.message || 'Could not generate export');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Reports"
        description="Structured Federation exports for leadership review."
        actions={
          report ? (
            <div className="flex flex-wrap gap-2 no-print">
              <button
                type="button"
                className={btnSecondary}
                onClick={async () => {
                  try {
                    const params = { type };
                    if (programId) params.programId = programId;
                    if (periodId) params.periodId = periodId;
                    const blob = await federation.downloadExportCsv(params);
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `tactmark-federation-${type}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch (e) {
                    setError(e.message || 'Could not download CSV');
                  }
                }}
              >
                Download CSV
              </button>
              <button type="button" className={btnSecondary} onClick={() => window.print()}>
                Print / save PDF
              </button>
            </div>
          ) : null
        }
      />

      <div className="border border-navy-200 rounded-xl bg-white p-5 mb-6 space-y-4 max-w-2xl no-print">
        {loadingMeta ? (
          <p className="text-sm text-gray-500">Loading filters…</p>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium text-navy-900 mb-1">Export type</label>
              <select className={inputClass} value={type} onChange={(e) => setType(e.target.value)}>
                {EXPORT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-navy-900 mb-1">Program</label>
                <select
                  className={inputClass}
                  value={programId}
                  onChange={(e) => setProgramId(e.target.value)}
                >
                  <option value="">All programs</option>
                  {programs.map((p) => (
                    <option key={p.id} value={p.id}>{safeText(p.name)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-900 mb-1">Reporting period</label>
                <select
                  className={inputClass}
                  value={periodId}
                  onChange={(e) => setPeriodId(e.target.value)}
                >
                  <option value="">No period filter</option>
                  {periods.map((p) => (
                    <option key={p.id} value={p.id}>
                      {safeText(p.name)} ({safeText(p.startDate)} – {safeText(p.endDate)})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <button type="button" className={btnPrimary} disabled={loading} onClick={generate}>
              {loading ? 'Generating…' : 'Generate report'}
            </button>
          </>
        )}
      </div>

      <ErrorBanner message={error} onRetry={generate} />

      {loading && <LoadingBlock label="Generating export…" />}

      {!loading && !report && !error && (
        <div className="border border-navy-200 rounded-xl bg-white px-6 py-10 text-center">
          <p className="text-navy-900 font-medium">No report generated yet</p>
          <p className="text-sm text-gray-500 mt-2">
            Choose an export type and optional program or reporting period, then generate.
          </p>
        </div>
      )}

      {report && (
        <div className="print:mt-0">
          <div className="mb-4">
            <h2 className="text-xl font-bold text-navy-900">
              {safeText(
                EXPORT_TYPES.find((t) => t.value === report.type)?.label || report.type,
                'Federation report'
              )}
            </h2>
            {report.selectedPeriod ? (
              <p className="text-sm text-gray-500 mt-1">
                Period: {safeText(report.selectedPeriod.name)} (
                {safeText(report.selectedPeriod.startDate)} – {safeText(report.selectedPeriod.endDate)})
              </p>
            ) : null}
          </div>

          {report.methodologyNote ? (
            <ReportSection title="Methodology">
              <p className="text-sm text-navy-800">{safeText(report.methodologyNote)}</p>
            </ReportSection>
          ) : null}

          {report.evidenceBoundaries ? (
            <ReportSection title="Evidence boundaries">
              <p className="text-sm text-navy-800 mb-2">{safeText(report.evidenceBoundaries.note)}</p>
              {renderValueRows(report.evidenceBoundaries)}
            </ReportSection>
          ) : null}

          <ReportSection title="Programs">
            <ListOrEmpty
              items={report.programs}
              empty="No programs in this export."
              renderItem={(p) => (
                <li key={p.id} className="text-sm text-navy-900">
                  {safeText(p.name)} · {safeText(p.programType)} · {safeText(p.status)}
                </li>
              )}
            />
          </ReportSection>

          <ReportSection title="Teams">
            <ListOrEmpty
              items={report.teams}
              empty="No teams in this export."
              renderItem={(t) => (
                <li key={t.id || t.teamId} className="text-sm text-navy-900">
                  {safeText(t.name || t.teamName, t.id || t.teamId)}
                </li>
              )}
            />
          </ReportSection>

          <ReportSection title="Findings">
            <ListOrEmpty
              items={report.findings}
              empty="No findings in this export."
              renderItem={(f) => (
                <li key={f.id} className="text-sm border-b border-navy-50 pb-2">
                  <p className="text-navy-900">{safeText(f.findingText || f.status)}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {safeText(f.status)} · Review: {safeText(f.reviewStatus)}
                  </p>
                </li>
              )}
            />
          </ReportSection>

          <ReportSection title="Program submissions">
            <ListOrEmpty
              items={report.submissions}
              empty="No program submissions in this export."
              renderItem={(submission) => (
                <li key={submission.id} className="text-sm border-b border-navy-50 pb-2">
                  <p className="font-medium text-navy-900">{safeText(submission.title)}</p>
                  <p className="text-gray-600 whitespace-pre-wrap">{safeText(submission.summary)}</p>
                  <p className="text-xs text-gray-500 mt-1">{safeText(submission.status)} · {safeText(submission.submittedAt)}</p>
                </li>
              )}
            />
          </ReportSection>

          <ReportSection title="Actions">
            <ListOrEmpty
              items={report.actions}
              empty="No actions in this export."
              renderItem={(a) => (
                <li key={a.id} className="text-sm border-b border-navy-50 pb-2">
                  <p className="text-navy-900">{safeText(a.title)}</p>
                  <p className="text-xs text-gray-500 mt-1">{safeText(a.status)}</p>
                </li>
              )}
            />
          </ReportSection>

          <ReportSection title="Reviews">
            <ListOrEmpty
              items={report.reviews}
              empty="No reviews in this export."
              renderItem={(r) => (
                <li key={r.id} className="text-sm border-b border-navy-50 pb-2">
                  <p className="text-navy-900">{safeText(r.reviewOutcome)}</p>
                  {r.reviewNotes ? (
                    <p className="text-xs text-gray-600 mt-1">{safeText(r.reviewNotes)}</p>
                  ) : null}
                </li>
              )}
            />
          </ReportSection>

          {report.programExecution ? (
            <ReportSection title="Program execution">
              {renderValueRows(report.programExecution)}
            </ReportSection>
          ) : null}

          <ReportSection title="Missing evidence">
            <ListOrEmpty
              items={report.missingEvidence}
              empty="No missing-evidence items flagged."
              renderItem={(m, idx) => (
                <li key={`${m.type}-${idx}`} className="text-sm text-navy-900">
                  {safeText(m.message || m.type)}
                </li>
              )}
            />
          </ReportSection>
        </div>
      )}
    </>
  );
}

export default function FederationReports() {
  return (
    <FederationGate>
      <ReportsInner />
    </FederationGate>
  );
}
